import React, { useState } from 'react';
import * as Yup from 'yup';
import Input from '../components/common/Input';
import Button from '../components/common/Button';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useLoginUserMutation } from '../redux/apiSlice';
import { toast } from 'react-toastify';
import { Loader2 } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { login } from '../redux/counterSlice';
import { loginSchema } from '../validations/SchemaValidations';
import SubscriptionExpiredModal from '../components/common/SubscriptionExpiredModal';
import AlreadyLoggedInModal from '../components/common/AlreadyLoggedInModal';
import SessionExpiredMessage from '../components/SessionExpiredMessage';
import { getDeviceInfo } from '../utils/deviceInfo';

type FormValues = {
  email: string;
  password: string;
  token?: string;
  user_device?: string;
};
interface LoginResult {
  access: string;
  refresh: string;
  user_info: any;
  survey_required: boolean;
  survey_id: number;
  message?: string;
}

const Login: React.FC = () => {
  const [searchParams] = useSearchParams();
  const isSessionExpired = searchParams.get('expired');

  const initialFormState: FormValues = {
    email: '',
    password: '',
  };

  const [formData, setFormData] = useState<FormValues>(initialFormState);
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const [loginUser, { isLoading }] = useLoginUserMutation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showExpiredModal, setShowExpiredModal] = useState(false);
  const [showAlreadyLoggedInModal, setShowAlreadyLoggedInModal] =
    useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
    setFormErrors((prevErrors) => ({ ...prevErrors, [name]: '' }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    try {
      const urlToken = searchParams.get('token');
      let tempFormData = { ...formData };
      await loginSchema.validate(formData, { abortEarly: false });
      setFormErrors({});

      if (urlToken) {
        tempFormData = { ...formData, token: urlToken };
      }

      // Add device information to the payload
      const userDevice = getDeviceInfo();
      tempFormData = {
        ...tempFormData,
        email: tempFormData.email.toLowerCase(),
        user_device: userDevice?.device_type || '',
      };

      const result = (await loginUser(tempFormData).unwrap()) as LoginResult;

      if (result.message === 'expired') {
        setShowExpiredModal(true);
        return;
      }

      if (result.message === 'already_logged_in') {
        setShowAlreadyLoggedInModal(true);
        return;
      }

      // toast.success('You are successfully logged in!');
      if (result.access) {
        dispatch(
          login({
            accessToken: result.access,
            refreshToken: result.refresh,
            user: result.user_info.id,
            userData: result.user_info,
          }),
        );
      }

      if (result.survey_required && result.survey_id) {
        console.log('redirecting to survey', result.survey_id);
        window.location.href = `/courses?survey=${result.survey_id}`;
        // navigate(`/courses?survey=${result.survey_id}`);
      } else {
        console.log('redirecting to courses');
        navigate('/courses');
      }
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        const errors: { [key: string]: string } = {};
        error.inner.forEach((err) => {
          if (err.path) {
            errors[err.path] = err.message;
          }
        });
        setFormErrors(errors);
      } else {
        toast.error(
          `Trouble Logging In?\n\nPlease check your email and password. If you've forgotten your password, click the "Forgot your password?" link.\n\nStill need help? Contact our support team at info@MyCodeDragon.com. We're here to assist you!`,
        );
      }

      console.error('Login failed:', error);
    }
  };

  const formStyles = 'mb-4';

  return (
    <>
      <div className="flex flex-col items-center justify-center min-h-[85vh] px-4 py-8">
        <div className="bg-white p-4 sm:p-8 shadow-[0_0_15px_rgba(0,0,0,0.2)] w-full max-w-[638px] rounded-lg">
          <h1 className="text-xl sm:text-[24px] font-bold mb-4">
            Welcome Back!
          </h1>

          <SessionExpiredMessage isVisible={!!isSessionExpired} />

          <form onSubmit={handleSubmit}>
            <div className={formStyles}>
              <label htmlFor="email" className="text-xs block font-medium">
                Email *
              </label>
              <Input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter your email"
                disabled={isLoading}
              />
              <p className="text-red-500 text-[13px] h-[4px]">
                {formErrors.email}
              </p>
            </div>
            <div className={formStyles}>
              <label htmlFor="password" className="text-xs block font-medium">
                Password *
              </label>
              <Input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter your password"
                disabled={isLoading}
              />
              <p className="text-red-500 text-[13px] h-[4px]">
                {formErrors.password}
              </p>
            </div>
            <div className="flex justify-center">
              <Button
                type="submit"
                className="w-full sm:w-[60%] h-[50px] flex items-center justify-center text-lg sm:text-[24px]"
                disabled={isLoading}
              >
                {isLoading ? (
                  <Loader2 className="animate-spin h-6 w-6" />
                ) : (
                  'Log in'
                )}
              </Button>
            </div>

            <div className="mt-2 text-center">
              <Link
                to="/forget-password"
                className="text-[12px] text-[#858585] hover:underline"
              >
                Forgot your password?
              </Link>
            </div>
          </form>
        </div>
        <div className="mt-6 text-center flex flex-col sm:flex-row items-center gap-4">
          <p className="text-xl sm:text-2xl md:text-[32px] text-[#4f4f4f] font-bold">
            Don&apos;t Have an Account Yet?
          </p>
          <Link to="/signup">
            <Button className="w-full sm:w-auto sm:min-w-[200px] md:min-w-[344px] text-xl sm:text-2xl md:text-[32px] bg-[#023f81]">
              Sign Up
            </Button>
          </Link>
        </div>
      </div>

      {showExpiredModal && (
        <SubscriptionExpiredModal onClose={() => setShowExpiredModal(false)} />
      )}
      {showAlreadyLoggedInModal && (
        <AlreadyLoggedInModal
          onClose={() => setShowAlreadyLoggedInModal(false)}
          email={formData.email?.toLowerCase() || ''}
        />
      )}
    </>
  );
};

export default Login;
